"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { ADSTERRA_ENABLED } from "@/components/nitro/config";

// Temporary explicit opt-in gate. Replace with a legally vetted CMP before broad rollout.
// Do not infer TCF consent from Nitro after Nitro's script has been disabled.
const STORAGE_KEY = "bfc_adsterra_opt_in_v1";
const ConsentContext = createContext<{ accepted: boolean; decide: (value: boolean) => void; reopen: () => void }>({ accepted: false, decide: () => {}, reopen: () => {} });

export function AdsterraConsentProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<"yes" | "no" | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    setChoice(saved === "yes" || saved === "no" ? saved : null);
    setOpen(saved !== "yes" && saved !== "no");
  }, []);
  const decide = (value: boolean) => {
    localStorage.setItem(STORAGE_KEY, value ? "yes" : "no");
    setChoice(value ? "yes" : "no");
    setOpen(false);
    if (!value) window.location.reload(); // Remove previously loaded third-party ad scripts.
  };
  if (!ADSTERRA_ENABLED) return <>{children}</>;
  return (
    <ConsentContext.Provider value={{ accepted: choice === "yes", decide, reopen: () => setOpen(true) }}>
      {children}
      <button type="button" onClick={() => setOpen(true)} style={{ position: "fixed", bottom: 8, left: 8, zIndex: 9990, fontSize: 12, padding: "6px 9px", color: "#fff", background: "#273644", border: "1px solid #64748b", borderRadius: 6 }}>Ad privacy choices</button>
      {open && (
        <div role="dialog" aria-modal="true" aria-label="Advertising privacy choices" style={{ position: "fixed", zIndex: 99999, inset: 0, background: "rgba(0,0,0,.75)", display: "flex", alignItems: "end", justifyContent: "center", padding: 12 }}>
          <div style={{ maxWidth: 540, width: "100%", background: "#15252f", border: "1px solid #607480", borderRadius: 12, color: "white", padding: 20 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>Advertising privacy</h2>
            <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>May we load ads from Adsterra? Their advertising partners may use cookies or similar technologies to deliver and measure ads. Choosing No means Adsterra ads will not load. You can change your choice with the Ad privacy choices button.</p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={() => decide(false)} style={{ padding: "10px 16px", background: "#364957", color: "white", borderRadius: 8 }}>No, thanks</button>
              <button type="button" onClick={() => decide(true)} style={{ padding: "10px 16px", background: "#a8edcb", color: "#09221a", fontWeight: 700, borderRadius: 8 }}>Allow advertising</button>
            </div>
            <p style={{ marginTop: 12, fontSize: 12 }}>See our Privacy Policy for additional information.</p>
          </div>
        </div>
      )}
    </ConsentContext.Provider>
  );
}

const ZONES = {
  square: { key: "8f7e2c1ff927de032de2fd41f1b112ed", width: 300, height: 250 },
  slim: { key: "aef1c26cd3e62c12a2250b3770be04b1", width: 320, height: 50 },
  desktop: { key: "c0d52d11c57c2c671b567d8396e0b58c", width: 728, height: 90 },
} as const;

type Format = keyof typeof ZONES;
export function AdsterraBanner({ format, position }: { format: Format; position: string }) {
  const { accepted } = useContext(ConsentContext);
  const [fits, setFits] = useState(false);
  const pathname = usePathname();
  const zone = ZONES[format];
  useEffect(() => {
    const query = format === "desktop" ? "(min-width: 768px)" : "(min-width: 320px) and (max-width: 767px)";
    const mq = matchMedia(query);
    const update = () => setFits(mq.matches && innerWidth >= zone.width);
    update(); mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [format, zone.width]);
  if (!ADSTERRA_ENABLED || !accepted || !fits) return null;
  // IFRAME SYNC script runs in its own document; global atOptions cannot collide
  // across multiple placements and SPA re-renders cannot duplicate document.write.
  const html = `<!doctype html><html><head><meta name="referrer" content="strict-origin-when-cross-origin"></head><body style="margin:0;padding:0;overflow:hidden"><script>var atOptions=${JSON.stringify({ key: zone.key, format: "iframe", height: zone.height, width: zone.width, params: {} })};<\/script><script src="https://bauval.org/22/${zone.key}"><\/script></body></html>`;
  return (
    <div data-adsterra-position={position} aria-label="Advertisement" style={{ display: "flex", justifyContent: "center", width: "100%", margin: "26px auto", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 11, opacity: .65, textTransform: "uppercase", letterSpacing: 1 }}>Advertisement</span>
      <iframe key={`${pathname}:${position}`} title={`Advertisement ${position}`} srcDoc={html} width={zone.width} height={zone.height} scrolling="no" style={{ width: zone.width, height: zone.height, border: 0, maxWidth: "100%" }} />
    </div>
  );
}

export function AdsterraSocialBar() {
  const { accepted } = useContext(ConsentContext);
  const pathname = usePathname();
  useEffect(() => {
    if (!ADSTERRA_ENABLED || !accepted || !pathname?.startsWith("/bases/")) return;
    const id = "bfc-adsterra-social-bar";
    if (document.getElementById(id)) return;
    const script = document.createElement("script");
    script.id = id;
    script.src = "https://bauval.org/14/24fc4be50064b76f50b4635bc4f03ae0";
    script.async = true;
    script.setAttribute("data-cfasync", "false");
    document.body.appendChild(script);
    return () => { /* Third-party overlays cannot be reliably torn down by removing the loader. */ };
  }, [accepted, pathname]);
  return null;
}
